import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { Box, Stack, Typography, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import { DataAccessProvider } from '@constants/constants';
import appsflyerLogo from '../../assets/icons/appsflyer-logo.png';
import adjustLogo from '../../assets/icons/adjust-logo.png';
import googleDriveLogo from '../../assets/icons/google-drive-logo.png';
import snowFlakeLogo from '../../assets/icons/snowflake-logo.png';
import bigQueryLogo from '../../assets/icons/big-query-logo.png';
import gcsLogo from '../../assets/icons/gcs-logo.png';
import s3Logo from '../../assets/icons/s3-logo.png';
import mySqlLogo from '../../assets/icons/mysql-logo.png';
import aggregatedCohortDataLogo from '../../assets/icons/aggregated-cohort-data-icon.png';

interface CustomAccordionProps {
  title: string;
  defaultOpen?: boolean;
  isSuccess?: boolean;
  children: ReactNode;
  open?: boolean;
  showEdit?: boolean;
  onEdit?: () => void;
  disableExpand?: boolean;
  provider?: DataAccessProvider;
  showProvider?: boolean;
  borderStyle?: string;
  hideExpand?: boolean;
}

const CustomAccordion: FC<CustomAccordionProps> = ({
  children,
  title,
  defaultOpen,
  isSuccess,
  open,
  showEdit,
  onEdit,
  disableExpand = false,
  provider,
  showProvider = false,
  borderStyle,
  hideExpand = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(!!defaultOpen);

  const getProviderDetails = useCallback(() => {
    switch (provider) {
      case DataAccessProvider.APPS_FLYER:
        return { iconUrl: appsflyerLogo, label: 'AppsFlyer' };
      case DataAccessProvider.ADJUST:
        return { iconUrl: adjustLogo, label: 'Adjust' };
      case DataAccessProvider.BIGQUERY:
        return { iconUrl: bigQueryLogo, label: 'BigQuery' };
      case DataAccessProvider.GCS:
        return { iconUrl: gcsLogo, label: 'Google Cloud Storage' };
      case DataAccessProvider.S3:
        return { iconUrl: s3Logo, label: 'Amazon S3' };
      case DataAccessProvider.MYSQL:
        return { iconUrl: mySqlLogo, label: 'My SQL' };
      case DataAccessProvider.SNOWFLAKE:
        return { iconUrl: snowFlakeLogo, label: 'Snowflake' };
      case DataAccessProvider.GOOGLE_DRIVE:
        return {
          iconUrl: googleDriveLogo,
          label: 'Direct File Share (Cloud Storage Link)',
        };
      case DataAccessProvider.AGGREGATED_DATA:
        return {
          iconUrl: aggregatedCohortDataLogo,
          label: 'Aggregated Cohort Data',
        };
      case DataAccessProvider.NONE:
        return { iconUrl: undefined, label: 'Other' };
      default:
        return { iconUrl: undefined, label: 'Other' };
    }
  }, [provider]);

  useEffect(() => {
    if (open !== undefined) {
      setIsExpanded(!!open);
    }
  }, [open]);

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        borderRadius: '1rem',
        border: borderStyle,
        padding: '1.5rem 1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        transition: 'all 0.3s ease-in-out',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography fontSize="1.15rem" lineHeight="1.375rem" fontWeight={700}>
            {title}
          </Typography>

          {showProvider && (
            <Box
              sx={{
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                justifyContent: 'center',
                color: '#162936',
                fontSize: '0.875rem',
                fontWeight: 700,
                lineHeight: '1.25rem',
                letterSpacing: '-0.35px',
              }}
            >
              {getProviderDetails().iconUrl && (
                <img
                  height={'100%'}
                  src={getProviderDetails().iconUrl || ''}
                  alt={`logo-${provider}`}
                />
              )}
              {getProviderDetails().label}
            </Box>
          )}

          {isSuccess && (
            <CheckCircleIcon
              sx={{
                color: '#2F736E',
                fontSize: '1.25rem',
              }}
            />
          )}

          {!isSuccess && showEdit && (
            <EditIcon
              sx={{
                color: '#2F736E',
                fontSize: '1.25rem',
                cursor: 'pointer',
              }}
              onClick={onEdit}
            />
          )}
        </Stack>

        {!showEdit && !hideExpand && (
          <ExpandMoreIcon
            onClick={() => !disableExpand && setIsExpanded((prev) => !prev)}
            sx={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease-in-out',
              cursor: disableExpand ? 'not-allowed' : 'pointer',
            }}
            aria-expanded={isExpanded}
          />
        )}
      </Stack>

      <Collapse
        in={isExpanded}
        timeout={200}
        unmountOnExit
        sx={{
          transition: 'height 0.3s ease-in-out, padding 0.3s ease-in-out',
        }}
      >
        <Box
          sx={{
            overflow: 'auto',
            paddingTop: isExpanded ? '0.75rem' : '0',
            transition: 'padding 0.3s ease-in-out',
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

export default CustomAccordion;
